import { ChangeDetectionStrategy, Component, OnInit, computed, effect, inject, input, untracked } from '@angular/core';
import { Router } from '@angular/router';


import { ItemAssetType, PlayerItemContentType, PlayerItemSource } from '@features/player/data-access/player.config';
import { CataloguesStore } from '@features/assets/data-access';
import { DetailsService } from '@features/details/data-access/details.service';
import { AssetList } from '@features/assets/components/asset-list/asset-list';
import { AssetPromoCatalogue, IPromoOpenedEvent } from '@features/assets/components/asset-promo-catalogue/asset-promo-catalogue';
import { CatalogueRowSkeleton } from '@features/assets/components/catalogue-row-skeleton/catalogue-row-skeleton';
import { IAssetListItem, applyCatalogueTileFlags, displayFormatFromImageTypeId } from '@features/assets/components/asset-card/asset-list-item.types';

/**
 * A single lazy catalogue row (promo block or carousel) for the home / videostore /
 * audiostore layouts. Rendered inside `@defer (on viewport)`, so this component is only
 * instantiated once the row scrolls near the viewport — that's what keeps the per-catalogue
 * `getDetails`/`getItems`/`getPromo` calls lazy instead of all firing on page load.
 *
 * It keeps its own skeleton visible until the fetch resolves: without that the row would
 * collapse to zero height during the request, pulling the rows below up into the viewport
 * and cascading every `@defer` trigger at once — defeating the lazy load entirely.
 */
@Component({
    selector: 'catalogue-row',
    templateUrl: './catalogue-row.html',
    standalone: true,
    imports: [AssetList, AssetPromoCatalogue, CatalogueRowSkeleton],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CatalogueRow implements OnInit {
    private readonly router = inject(Router);
    private readonly detailsService = inject(DetailsService);
    private readonly store = inject(CataloguesStore);

    readonly referenceId = input.required<string>();
    readonly isPromo = input(false);

    /** Raw /details entry: undefined until it answers, null when it answered 404. */
    private readonly detailsEntry = computed(() => this.store.details()[this.referenceId()]);
    protected readonly details = computed(() => this.detailsEntry() ?? null);
    /**
     * The catalogue is gone: /details answered 404. Happens when it is still referenced by
     * the homepage / root structure but has since been deactivated in backoffice. Nothing
     * left to render, and nothing to keep waiting for.
     */
    private readonly missing = computed(() => this.detailsEntry() === null);
    protected readonly items = computed(() => this.store.items()[this.referenceId()]?.items ?? []);
    protected readonly promo = computed(() => this.store.promo()[this.referenceId()] ?? null);
    protected readonly displayFormat = computed(
        () => displayFormatFromImageTypeId(this.details()?.childrenImageTypeId),
    );

    /** /items answered, and answered empty — the row has nothing to render from items alone. */
    private readonly itemsEmpty = computed(() => {
        const dto = this.store.items()[this.referenceId()];
        return dto !== undefined && !dto?.items?.length;
    });

    /**
     * A catalogue that holds subcatalogues never shows items — it shows its subcatalogues as
     * tiles (same rule as the catalogue page and `catalogue-carousel`). `hasSubcatalogues` from
     * /details is the authority; an empty /items response is the fallback trigger, so a row
     * whose catalogue reports the flag wrong still resolves to its subcatalogues instead of
     * rendering nothing at all.
     */
    protected readonly showsSubcatalogues = computed(
        () => !this.missing() && ((this.details()?.hasSubcatalogues ?? false) || this.itemsEmpty()),
    );

    /** Subcatalogue tiles mapped to the asset-card shape; imageUrl serves every display format. */
    private readonly subcatalogueTiles = computed<readonly IAssetListItem[]>(() => {
        const subs = this.store.subcatalogues()[this.referenceId()]?.items;
        return subs?.map(s => ({
            referenceId: s.referenceId,
            title: s.title,
            hideTitle: s.hideTitle,
            posterLandscape: s.imageUrl,
            posterPortrait: s.imageUrl,
            posterSquare: s.imageUrl,
            // Marks the tile as a catalogue so the MiniDetails hover directive skips it.
            contentType: 'catalogue',
        } as IAssetListItem)) ?? [];
    });

    /** What the carousel renders: subcatalogue tiles for a container, items for a leaf. */
    protected readonly tiles = computed<readonly IAssetListItem[]>(() => {
        // A catalogue whose /details 404'd is gone regardless of what /items answered: the
        // two calls are fired in parallel (see ngOnInit), and /items still returns content
        // for a deactivated catalogue - which rendered as a carousel with no title.
        if (this.missing()) return [];
        const base = this.showsSubcatalogues() ? this.subcatalogueTiles() : this.items();
        // The catalogue's own "hide title" (backoffice) hides EVERY tile's title in this
        // row, the way the mobile apps do — both leaf items and subcatalogue tiles. The
        // per-tile `hideTitle` alone missed it, so a catalogue flagged hide-title still
        // showed titles on the web (AVIION report: leaf "Svi filmovi" once its items were
        // republished with item-level hideTitle=false, and container "Nestflix.fun" whose
        // subcatalogue tiles carry their own flag). OR the catalogue flag over each tile's.
        // "Hide badge" follows the same rule (AVIION-37342).
        return applyCatalogueTileFlags(base, this.details());
    });

    /** True once this row's fetch has resolved (the key exists in the store map, even if empty). */
    protected readonly loaded = computed(() => {
        const id = this.referenceId();
        if (this.isPromo()) return this.store.promo()[id] !== undefined;
        // Which list matters is only known once /details answers — resolving on /items alone
        // would drop the skeleton and collapse a container row to zero height for the length
        // of the /subcatalogues call. Key presence, not truthiness: a 404 resolves to null,
        // and that row is finished — it renders nothing rather than waiting forever.
        if (this.detailsEntry() === undefined) return false;
        if (this.missing()) return true;
        return this.showsSubcatalogues()
            ? this.store.subcatalogues()[id] !== undefined
            : this.store.items()[id] !== undefined;
    });

    constructor() {
        // /items is fired unconditionally in ngOnInit so the common (leaf) row keeps loading
        // details and items in parallel — gating that call on /details would cost every row on
        // the page an extra round trip. Once we know the row holds subcatalogues (flagged by
        // /details, or implied by an empty /items) we fetch what it actually renders; the empty
        // /items response is simply ignored.
        effect(() => {
            if (this.isPromo() || !this.showsSubcatalogues()) return;
            const id = this.referenceId();
            untracked(() => {
                if (this.store.subcatalogues()[id] === undefined) this.store.loadSubcatalogues(id);
            });
        });
    }

    ngOnInit(): void {
        // Fires only when the row enters the viewport (see the `@defer` wrapper in the host
        // page). Guarded against the cached case (revisits / already-loaded keys) so we don't
        // re-issue requests; the store's keyed loaders use mergeMap, so parallel rows entering
        // at once don't cancel each other.
        const id = this.referenceId();
        if (this.isPromo()) {
            if (this.store.promo()[id] === undefined) this.store.loadPromo(id);
        } else {
            if (this.store.details()[id] === undefined) this.store.loadDetails(id);
            // Skipped once /details is known to have 404'd (another row on the page, or an
            // earlier visit, already asked). On a first encounter the two still race, and
            // tiles() discards whatever /items returns.
            if (!this.missing() && this.store.items()[id] === undefined) this.store.loadItems(id);
        }
    }

    protected onItemClick(item: IAssetListItem): void {
        // A subcatalogue tile has no details to open — it drills into that catalogue, the same
        // navigation the row's title click and the catalogue page's own tiles use.
        if (this.showsSubcatalogues()) {
            if (item.referenceId) this.router.navigate(['catalogue', item.referenceId]);
            return;
        }
        // Legacy details modal — opens the existing details overlay via the hash facade.
        this.detailsService.open({ item });
    }

    protected onPromoOpened(event: IPromoOpenedEvent): void {
        const block = event.promo.promoBlock;
        // A promoted item opens its details directly; otherwise fall back to the catalogue grid
        // (ported from the rc promo `openCatalogue`).
        if (block?.promotedItemReferenceId && block.promotedItemType) {
            this.detailsService.open({
                item: {
                    referenceId: block.promotedItemReferenceId,
                    contentType: this.promoContentType(block.promotedItemType),
                },
            });
        } else {
            this.router.navigate(['catalogue', event.referenceId]);
        }
    }

    private promoContentType(type: number): string {
        switch (type) {
            case ItemAssetType.Vod:     return PlayerItemSource.SVOD;
            case ItemAssetType.Series:  return PlayerItemContentType.SERIES;
            case ItemAssetType.Epg:     return PlayerItemSource.THEPG;
            case ItemAssetType.Episode: return PlayerItemContentType.EPISODE;
            case ItemAssetType.Aod:     return PlayerItemSource.AOD;
            default:                    return PlayerItemSource.SVOD;
        }
    }

    protected goToCatalogue(): void {
        this.router.navigate(['catalogue', this.referenceId()]);
    }
}
